/**
 * Tidal stream overlay — draws the arrow set for whichever SOURCE is selected
 * (chart diamonds or RYA eNav), one source at a time. The arrows are computed
 * upstream by `streamArrowsAt(source, time)` so this layer just renders them.
 *
 * Exact charted diamonds draw solid; eNav "secondary" coverage points draw
 * lighter and thinner because their positions are georeferenced (~1 km approx),
 * not charted. Slack (rate below threshold) shows a marker, not an arrow.
 * Real RYA training data; not for real-world navigation (caption says so).
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDestination } from '@/nav';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';
import type { StreamArrow } from '@/tides';

const SOURCE_ID = 'tide-stream';
const NM_PER_KN = 0.35;
const MAX_ARROW_NM = 1.6;
const SLACK_KN = 0.05;

function build(arrows: StreamArrow[], on: boolean): FeatureCollection {
  if (!on) return { type: 'FeatureCollection', features: [] };
  const features: Feature[] = [];
  for (const a of arrows) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: a.position },
      properties: { kind: 'point', approx: a.approx, label: a.label, slack: a.rateKn < SLACK_KN },
    });
    if (a.rateKn < SLACK_KN) continue;
    const lenNm = Math.min(MAX_ARROW_NM, a.rateKn * NM_PER_KN);
    const tip = rhumbDestination(a.position, a.setDeg, lenNm);
    const left = rhumbDestination(tip, a.setDeg + 150, lenNm * 0.22);
    const right = rhumbDestination(tip, a.setDeg - 150, lenNm * 0.22);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [a.position, tip] },
      properties: { kind: 'arrow', approx: a.approx },
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [left, tip, right] },
      properties: { kind: 'arrow', approx: a.approx },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function ensureTideLayer(
  map: MaplibreMap,
  on: boolean,
  arrows: StreamArrow[],
  palette: Palette,
): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(arrows, on) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(arrows, on));
  }
  if (!map.getLayer('tide-stream')) {
    map.addLayer({
      id: 'tide-stream',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'arrow'],
      paint: {
        'line-color': p.CHMGD,
        // Secondary (approx) arrows draw thinner and softer than exact diamonds.
        'line-width': ['case', ['get', 'approx'], 1.1, 1.8],
        'line-opacity': ['case', ['get', 'approx'], 0.5, 0.95],
      },
    });
  }
  if (!map.getLayer('tide-diamond')) {
    map.addLayer({
      id: 'tide-diamond',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'point'],
      paint: {
        'circle-radius': ['case', ['get', 'approx'], 2, 3],
        'circle-color': ['case', ['get', 'approx'], 'rgba(204,31,143,0)', p.CHMGD],
        'circle-stroke-color': p.CHMGD,
        'circle-stroke-width': ['case', ['get', 'approx'], 1, 1],
        'circle-opacity': ['case', ['get', 'approx'], 0.5, 1],
        'circle-stroke-opacity': ['case', ['get', 'approx'], 0.6, 1],
      },
    });
  }
  if (!map.getLayer('tide-label')) {
    map.addLayer({
      id: 'tide-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'point'],
      minzoom: 10,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': p.CHMGD,
        'text-halo-color': p.UI_BG,
        'text-halo-width': 1.3,
        'text-opacity': ['case', ['get', 'approx'], 0.7, 1],
      },
    });
  }
}

export function updateTideLayer(map: MaplibreMap, on: boolean, arrows: StreamArrow[]): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(arrows, on));
}
